import promptsConfig from "../../config/ai-prompts.json";

export interface AIPromptType {
	overall_context: string;
	reflection_qa: string;
	reflection_summary: string;
	suggested_reflection: string;
	contributing_factors: string;
	signals: string;
	corrective_actions: string;
	future_state: string;
	checkin_analysis: string;
	reflection_analysis: string;
	mentee_scoring: string;
	scoring_config: string;
}

export type AIPromptKey = keyof AIPromptType;

export type OperationKey =
	| "checkin_analysis"
	| "conversation"
	| "reflection_analysis";

export type OpenAIParams = {
	temperature?: number;
	max_tokens?: number;
	top_p?: number;
	presence_penalty?: number;
	frequency_penalty?: number;
	response_format?: any;
};

// Static constants - loaded at module initialization
export const OPENAI_MODEL: string = promptsConfig.openai?.model || "gpt-4o";

export const ALL_PROMPTS: AIPromptType = (() => {
	const prompts: Partial<AIPromptType> = {};
	for (const [key, value] of Object.entries((promptsConfig as any).prompts)) {
		(prompts as any)[key as AIPromptKey] = (value as any).prompt;
	}
	return prompts as AIPromptType;
})();

export const OPENAI_DEFAULTS: OpenAIParams =
	(promptsConfig.openai as any)?.defaults || {};

export const OPENAI_PER_PROMPT: Record<AIPromptKey, OpenAIParams> = (() => {
	const perPrompt = (promptsConfig.openai as any)?.perPrompt || {};
	const result: Partial<Record<AIPromptKey, OpenAIParams>> = {};
	for (const [key, value] of Object.entries(perPrompt)) {
		result[key as AIPromptKey] = value as OpenAIParams;
	}
	return result as Record<AIPromptKey, OpenAIParams>;
})();

export const PROMPT_TIMEOUTS: Record<keyof AIPromptType, number> = (() => {
	const promptTimeouts =
		(promptsConfig as any)?.client?.timeouts?.promptTypes || {};
	const result: Partial<Record<keyof AIPromptType, number>> = {};
	for (const [key, value] of Object.entries(promptTimeouts)) {
		result[key as keyof AIPromptType] = value as number;
	}
	// Fill in defaults for missing values
	const defaultTimeout = 60000;
	const allPromptTypes: (keyof AIPromptType)[] = [
		"overall_context",
		"reflection_qa",
		"reflection_summary",
		"suggested_reflection",
		"contributing_factors",
		"signals",
		"corrective_actions",
		"future_state",
		"checkin_analysis",
		"reflection_analysis",
		"mentee_scoring",
		"scoring_config",
	];
	for (const type of allPromptTypes) {
		if (!(type in result)) {
			result[type] = defaultTimeout;
		}
	}
	return result as Record<keyof AIPromptType, number>;
})();

export const OPERATION_TIMEOUTS: Record<OperationKey, number> = (() => {
	const opTimeouts = (promptsConfig as any)?.client?.timeouts?.operations || {};
	const defaults: Record<OperationKey, number> = {
		checkin_analysis: 75000, // Reduced from 90s to 75s to account for edge function timeout reduction
		conversation: 60000,
		reflection_analysis: 120000,
	};
	const result: Partial<Record<OperationKey, number>> = {};
	for (const key of Object.keys(defaults) as OperationKey[]) {
		result[key] = opTimeouts[key] ?? defaults[key];
	}
	return result as Record<OperationKey, number>;
})();

export const CHECKIN_ANALYSIS_MAX_CHECKINS: number = (() => {
	const configured = (promptsConfig as any)?.client?.limits?.checkin_analysis
		?.max_checkins;
	return typeof configured === "number" && configured > 0 ? configured : 30;
})();

export const CHECKIN_ANALYSIS_MAX_MONTHS: number = (() => {
	const configured = (promptsConfig as any)?.client?.limits?.checkin_analysis
		?.max_months;
	return typeof configured === "number" && configured > 0 ? configured : 3;
})();

export const CONFIG_INFO = {
	version: (promptsConfig as any).version,
	lastUpdated: (promptsConfig as any).lastUpdated,
	description: (promptsConfig as any).description,
} as const;

// Helper functions for backward compatibility and convenience
// These are thin wrappers around static constants

/**
 * Get the OpenAI model configured for this application
 * @returns The model name (e.g., "gpt-4o")
 */
export function getOpenAIModel(): string {
	return OPENAI_MODEL;
}

/**
 * Get all AI prompts from the configuration file
 * @returns Object with all prompts keyed by type
 */
export function getAllPrompts(): AIPromptType {
	return ALL_PROMPTS;
}

/**
 * Get a specific prompt by type
 * @param type - The prompt type to retrieve
 * @returns The prompt string
 * @throws Error if prompt type doesn't exist
 */
export function getPrompt(type: AIPromptKey): string {
	const prompt = ALL_PROMPTS[type];

	if (!prompt) {
		throw new Error(
			`Prompt type '${type}' not found in configuration. Please check config/ai-prompts.json`,
		);
	}

	return prompt;
}

/**
 * Get prompt metadata (name and description)
 * @param type - The prompt type
 * @returns Object with name and description
 */
export function getPromptMetadata(type: AIPromptKey): {
	name: string;
	description: string;
} {
	const promptConfig = (promptsConfig as any).prompts[type];

	if (!promptConfig) {
		throw new Error(`Prompt type '${type}' not found in configuration`);
	}

	return {
		name: promptConfig.name,
		description: promptConfig.description,
	};
}

/**
 * Get default + per-prompt OpenAI parameters
 */
export function getOpenAIParamsForPrompt(type?: AIPromptKey): OpenAIParams {
	const defaults = OPENAI_DEFAULTS;
	const perPrompt = type ? OPENAI_PER_PROMPT[type] || {} : {};
	return { ...defaults, ...perPrompt };
}

/**
 * Get configuration version info
 */
export function getConfigInfo() {
	return CONFIG_INFO;
}

/**
 * Client-side timeout helpers
 */
export function getClientTimeoutForPromptType(
	type: keyof AIPromptType,
): number {
	return PROMPT_TIMEOUTS[type] ?? 60000;
}

export function getClientTimeoutForOperation(operation: OperationKey): number {
	return OPERATION_TIMEOUTS[operation] ?? 60000;
}

/**
 * Get the maximum number of check-ins to include in analysis
 */
export function getCheckInAnalysisMaxCheckins(): number {
	return CHECKIN_ANALYSIS_MAX_CHECKINS;
}

export function getCheckInAnalysisMaxMonths(): number {
	return CHECKIN_ANALYSIS_MAX_MONTHS;
}
